/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    createContext,
    useContext,
    useState,
    useMemo,
    useCallback,
    type ReactNode,
} from 'react';
import type {
    WizardStep,
    UserAttribute,
    VisitFrequency,
    WizardFlags,
    PassRecommendation,
} from '../types/wizard.types';
import { getPassRecommendations } from '../utils/decisionTree';

interface WizardContextValue {
    // State
    isOpen: boolean;
    showCloseButton: boolean;
    currentStep: WizardStep;
    residentAnswer: boolean | null;
    selectedAttributes: UserAttribute[];
    frequencyAnswer: VisitFrequency | null;
    canProceed: boolean;
    recommendations: PassRecommendation[];
    flags: WizardFlags;

    // Actions
    openWizard: () => void;
    closeWizard: () => void;
    hideCloseButton: () => void;
    nextStep: () => void;
    previousStep: () => void;
    setIsUsResident: (value: boolean) => void;
    toggleAttribute: (attribute: UserAttribute) => void;
    setVisitFrequency: (value: VisitFrequency) => void;
    resetWizard: () => void;
}

const WizardContext = createContext<WizardContextValue | undefined>(undefined);

interface WizardProviderProps {
    children: ReactNode;
    flags: WizardFlags;
}

function WizardProvider({ children, flags }: WizardProviderProps) {
    const firstStep: WizardStep = flags.disclaimerEnabled ? 'disclaimer' : 'residency';

    const [isOpen, setIsOpen] = useState(false);
    const [showCloseButton, setIsShowCloseButton] = useState(true);
    const [currentStep, setCurrentStep] = useState<WizardStep>(firstStep);
    const [residentAnswer, setResidentAnswer] = useState<boolean | null>(null);
    const [selectedAttributes, setSelectedAttributes] = useState<UserAttribute[]>([]);
    const [frequencyAnswer, setFrequencyAnswer] = useState<VisitFrequency | null>(null);

    const recommendations = getPassRecommendations({
        residentAnswer,
        selectedAttributes,
        frequencyAnswer,
    });

    const canProceed = (() => {
        switch (currentStep) {
            case 'disclaimer':
                return true;
            case 'residency':
                return residentAnswer !== null;
            case 'attributes':
                return selectedAttributes.length > 0;
            case 'visit-frequency':
                return frequencyAnswer !== null;
            case 'results':
                return true;
            default:
                return false;
        }
    })();

    const resetWizard = useCallback(() => {
        setCurrentStep(firstStep);
        setResidentAnswer(null);
        setSelectedAttributes([]);
        setFrequencyAnswer(null);
    }, [firstStep]);

    const openWizard = useCallback(() => {
        setIsOpen(true);
    }, []);

    const hideCloseButton = useCallback(() => {
        setIsShowCloseButton(false);
    }, []);

    const closeWizard = useCallback(() => {
        setIsOpen(false);
        resetWizard();
    }, [resetWizard]);

    const nextStep = useCallback(() => {
        if (!canProceed) return;

        switch (currentStep) {
            case 'disclaimer':
                setCurrentStep('residency');
                break;
            case 'residency':
                if (residentAnswer === false) {
                    setCurrentStep('visit-frequency');
                } else {
                    setCurrentStep('attributes');
                }
                break;
            case 'attributes':
                if (selectedAttributes.includes('none')) {
                    setCurrentStep('visit-frequency');
                } else {
                    setCurrentStep('results');
                }
                break;
            case 'visit-frequency':
                setCurrentStep('results');
                break;
            default:
                break;
        }
    }, [canProceed, currentStep, residentAnswer, selectedAttributes]);

    const previousStep = useCallback(() => {
        switch (currentStep) {
            case 'disclaimer':
                // First step, no previous
                break;
            case 'residency':
                if (flags.disclaimerEnabled) {
                    setCurrentStep('disclaimer');
                }
                break;
            case 'attributes':
                setCurrentStep('residency');
                break;
            case 'visit-frequency':
                if (residentAnswer === false) {
                    setCurrentStep('residency');
                } else {
                    // US resident who selected "none"
                    setCurrentStep('attributes');
                }
                break;
            case 'results':
                // Go back to the last question step
                if (residentAnswer === false) {
                    setCurrentStep('visit-frequency');
                } else if (selectedAttributes.includes('none')) {
                    setCurrentStep('visit-frequency');
                } else {
                    setCurrentStep('attributes');
                }
                break;
            default:
                break;
        }
    }, [currentStep, flags.disclaimerEnabled, residentAnswer, selectedAttributes]);

    const setIsUsResident = useCallback((value: boolean) => {
        setResidentAnswer(value);
    }, []);

    const toggleAttribute = useCallback((attribute: UserAttribute) => {
        setSelectedAttributes((prev) => {
            // "None" is mutually exclusive with all others
            if (attribute === 'none') {
                return prev.includes('none') ? [] : ['none'];
            }

            // Selecting any attribute clears "none"
            const withoutNone = prev.filter((a) => a !== 'none');

            if (withoutNone.includes(attribute)) {
                return withoutNone.filter((a) => a !== attribute);
            }
            return [...withoutNone, attribute];
        });
    }, []);

    const setVisitFrequency = useCallback((value: VisitFrequency) => {
        setFrequencyAnswer(value);
    }, []);

    const value: WizardContextValue = useMemo(
        () => ({
            isOpen,
            showCloseButton,
            currentStep,
            residentAnswer,
            selectedAttributes,
            frequencyAnswer,
            canProceed,
            recommendations,
            flags,
            openWizard,
            closeWizard,
            hideCloseButton,
            nextStep,
            previousStep,
            setIsUsResident,
            toggleAttribute,
            setVisitFrequency,
            resetWizard,
        }),
        [
            isOpen,
            showCloseButton,
            currentStep,
            residentAnswer,
            selectedAttributes,
            frequencyAnswer,
            canProceed,
            recommendations,
            flags,
            openWizard,
            closeWizard,
            hideCloseButton,
            nextStep,
            previousStep,
            setIsUsResident,
            toggleAttribute,
            setVisitFrequency,
            resetWizard,
        ]
    );

    return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

function useWizardContext(): WizardContextValue {
    const context = useContext(WizardContext);
    if (context === undefined) {
        throw new Error('useWizardContext must be used within a WizardProvider');
    }
    return context;
}

export { WizardProvider, useWizardContext };
